import { Alert, AlertAction, AlertDescription, AlertTitle } from '@ophelos/ui/components/alert';
import { Button } from '@ophelos/ui/components/button';
import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAgentChat } from '@/apps/ai/hooks/useAgentChat';
import { ChatInput } from '@/components/ai-chat/_shared/ChatInput';
import { type ChatMessage, ChatMessages } from '@/components/ai-chat/_shared/ChatMessages';
import { WelcomeScreen } from '@/components/ai-chat/_shared/WelcomeScreen';
import { getAgentConfig, type StarterPrompt } from '@/components/ai-chat/agent-registry';
import { useAIChat } from '@/contexts/AIChatContext';

const USER_GUIDE_AGENT_ID = 'user-guide-agent';

const SUPPORT_PROMPT: StarterPrompt = { id: 'open-support', text: "Something doesn't work", icon: 'bug' };

const PAGE_PROMPTS: Record<string, StarterPrompt[]> = {
  customers: [
    { text: 'How do I find a specific customer or case?', icon: 'search' },
    { text: 'Explain the different case statuses and what they mean.', icon: 'search' },
    { text: 'How do I update contact details for a customer?', icon: 'search' },
  ],
  campaigns: [
    { text: 'How do I create a new campaign?', icon: 'search' },
    { text: 'What are the different campaign types available?', icon: 'search' },
    { text: 'How do I track campaign performance?', icon: 'search' },
  ],
  files: [
    { text: 'How does file ingestion work?', icon: 'search' },
    { text: 'What file formats are supported for upload?', icon: 'search' },
    { text: 'How do I check the status of a file import?', icon: 'search' },
  ],
  analytics: [
    { text: 'What metrics are being tracked here?', icon: 'search' },
    { text: 'How do I export data or schedule a report?', icon: 'search' },
    { text: 'Explain the analytics dashboard indicators.', icon: 'search' },
  ],
  library: [
    { text: 'What templates are available in the library?', icon: 'search' },
    { text: 'How do I create or edit a communication template?', icon: 'search' },
    { text: 'How are templates used in campaigns?', icon: 'search' },
  ],
  settings: [
    { text: 'How do I update my notification preferences?', icon: 'search' },
    { text: 'What settings can I configure for my account?', icon: 'search' },
    { text: 'How do I change system-wide defaults?', icon: 'search' },
  ],
  ai: [
    { text: 'What AI agents are available and what do they do?', icon: 'search' },
    { text: 'How do I review past AI conversations?', icon: 'search' },
    { text: 'How does AI assist with case management?', icon: 'search' },
  ],
};

function getPagePromptsFromPath(pathname: string): StarterPrompt[] {
  // Settings lives at /settings, not /app/settings
  if (pathname.startsWith('/settings')) {
    return [...PAGE_PROMPTS.settings, SUPPORT_PROMPT];
  }
  const appMatch = pathname.match(/^\/app\/([^/]+)/);
  const appKey = appMatch?.[1];
  if (appKey && appKey in PAGE_PROMPTS) {
    return [...PAGE_PROMPTS[appKey], SUPPORT_PROMPT];
  }
  return [
    { text: 'How do I navigate the dashboard?', icon: 'search' },
    { text: 'Explain the case lifecycle in Forest.', icon: 'search' },
    { text: 'What reports are available to me?', icon: 'search' },
    SUPPORT_PROMPT,
  ];
}

export function DefaultChatContent() {
  const { messages: agentMessages, streaming, error, sendMessage, clearError } = useAgentChat(USER_GUIDE_AGENT_ID, 'agent');
  const { open: openAIChat } = useAIChat();
  const location = useLocation();

  const contextualPrompts = useMemo(() => getPagePromptsFromPath(location.pathname), [location.pathname]);

  const messages = useMemo<ChatMessage[]>(
    () =>
      agentMessages.map((m) => ({
        id: m.id,
        role: m.role as 'user' | 'assistant',
        content: m.content,
      })),
    [agentMessages]
  );

  const handleSend = useCallback(
    (content: string) => {
      sendMessage(content);
    },
    [sendMessage]
  );

  const handleChoosePrompt = useCallback(
    (prompt: StarterPrompt) => {
      if (prompt.id === 'open-support') {
        openAIChat({ source: 'support' });
      } else {
        handleSend(prompt.text);
      }
    },
    [openAIChat, handleSend]
  );

  const agentConfig = getAgentConfig('default');
  const contextualConfig = useMemo(() => ({ ...agentConfig, starterPrompts: contextualPrompts }), [agentConfig, contextualPrompts]);

  return (
    <>
      <div className="flex-1 overflow-hidden">
        <ChatMessages
          messages={messages}
          streaming={streaming}
          agentConfig={agentConfig}
          emptyState={<WelcomeScreen agentConfig={contextualConfig} onChoosePrompt={handleChoosePrompt} disabled={streaming?.active} />}
        />
      </div>
      <div className="shrink-0 stack gap-2">
        {error && (
          <Alert variant="destructive">
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
            <AlertAction>
              <Button variant="destructive" size="sm" onClick={clearError}>
                Dismiss
              </Button>
            </AlertAction>
          </Alert>
        )}
        <ChatInput onSend={handleSend} disabled={streaming?.active} />
      </div>
    </>
  );
}
